import { startCase } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { ApolloError } from '@apollo/client';
import {
	PaymentElement,
	useElements,
	useStripe
} from '@stripe/react-stripe-js';
import {
	PaymentIntent,
	StripePaymentElementChangeEvent
} from '@stripe/stripe-js';

import Button from '../../../../GlobalComponents/Button';
import Flex from '../../../../GlobalComponents/Flex';
import { SubTitle } from '../../../../GlobalComponents/Title';
import { useAppDispatch, useAppSelector } from '../../../../reduxstore/hooks';
import { centsToMoney } from '../../../../utils/centsToMoney';
import {
	CardAddBalanceInput,
	useCardUpsertAddBalanceMutation
} from '../../../graphql';
import { updateCard } from '../../../state/profileSlice';
import ApproveTransactionModal from '../components/ApproveTransactionModal';
import StatusModal from '../components/StatusModal';
import { PaymentIntentDetails } from '../../../../Checkout/views/PaymentGateway';

interface PaymentFormProps {
	giftCardNumber: number;
	balanceToAdd: number;
	onClose?: () => void;
	onBack?: () => void;

	paymentDetails: PaymentIntentDetails;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
	paymentDetails,
	giftCardNumber,
	balanceToAdd,
	onClose,
	onBack
}) => {
	const dispatch = useAppDispatch();
	const userId = useAppSelector(state => state.authReducer.id);
	const profile = useAppSelector(state => state.accountReducer.profile);
	const stripe = useStripe();
	const elements = useElements();
	const [isLoading, setIsLoading] = useState(false);
	const [statusMessage, setStatusMessage] = useState('');
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
	const isMobile = useMedia('(max-width: 768px)');

	const [showApproveModal, setShowApproveModal] = useState<boolean>(false);
	const [showStatusModal, setShowStatusModal] = useState(false);
	const cardData = profile.cards.find(
		card => card.card_number === giftCardNumber
	) as Card;

	const [upsertAddBalance, { loading: isProcessing }] =
		useCardUpsertAddBalanceMutation({
			errorPolicy: 'none'
		});

	useEffect(() => {
		if (!stripe || !elements || !paymentDetails.clientSecret) {
			return;
		}
	}, [paymentDetails.clientSecret, elements, stripe]);

	const handleSubmit = async () => {
		if (!elements) {
			return;
		}
		const { error: submitError } = await elements.submit();
		if (submitError) {
			// Show error to your customer
			return;
		}
		if (selectedPaymentMethod === 'card') {
			setShowApproveModal(true);
		} else {
			doPayment();
		}
	};

	const doPayment = async () => {
		if (!stripe || !elements) {
			return;
		}

		setIsLoading(true);

		stripe
			.confirmPayment({
				elements,
				redirect: 'if_required'
			})
			.then(result => {
				if (
					!result.error &&
					result.paymentIntent.status === 'succeeded'
				) {
					onProcessTrans(result.paymentIntent);
				} else if (result.error) {
					setShowStatusModal(true);
					setStatusMessage(
						result.error.message ?? 'An error occurred'
					);
					setIsLoading(false);
				}
			})
			.catch(error => {
				setShowStatusModal(true);
				setStatusMessage(error.message);
			});
	};

	const onProcessTrans = async (paymentIntent: PaymentIntent) => {
		let paymentSuccess = false;
		try {
			const inputValues: CardAddBalanceInput = {
				currentBalance: Number(cardData.cash),
				giftCardNumber: cardData.card_number,
				balanceToAdd: balanceToAdd,
				token: '',
				app_user_id: Number(userId),
				profile_id: Number(profile.id),
				session_id: '',
				stripe_trans_id: paymentIntent.id,
				id: paymentDetails.id,
				paymentIntentId: paymentDetails.paymentIntentId
			};

			const res = await upsertAddBalance({
				variables: {
					cardInfo: inputValues
				}
			});

			if (res?.data?.upsertAddBalance) {
				const updatedCardData = res.data.upsertAddBalance
					.card as IUpdatedCardData;

				const updatedCard = {
					...cardData,
					cash: +centsToMoney(updatedCardData.balance)
				} as Card;

				dispatch(updateCard(updatedCard));

				setShowStatusModal(true);
				setStatusMessage(
					`You successfully added $${centsToMoney(
						inputValues.balanceToAdd
					)} to card # ${inputValues.giftCardNumber}!`
				);
				// onClose && onClose();
				paymentSuccess = true;
				setIsLoading(false);
			}
		} catch (err) {
			paymentSuccess = false;
			if (err === 'undefined' || err === undefined) {
				setShowStatusModal(true);
				setStatusMessage(
					'We could not reach our servers at the moment. Please try again later. Your card was not charged.'
				);

				return paymentSuccess;
			}

			const error = err as ApolloError;

			if (
				error.message ===
				'Response not successful: Received status code 500'
			) {
				setShowStatusModal(true);
				setStatusMessage(
					'We could not reach our servers at the moment. Please try again later. Your card was not charged.'
				);

				return paymentSuccess;
			}

			setShowStatusModal(true);
			setIsLoading(false);
			setStatusMessage(error.message);
		}

		return paymentSuccess;
	};

	const onExitApproveModal = () => {
		setShowApproveModal(false);
		setShowStatusModal(false);
	};

	const toggleApproveModal = () => {
		setShowApproveModal(!showApproveModal);
	};

	const closeStatusModel = () => {
		setShowStatusModal(false);
		onClose && onClose();
	};

	const handlePaymentMethodChange = (
		event: StripePaymentElementChangeEvent
	) => {
		setSelectedPaymentMethod(event.value.type);
	};

	return (
		<>
			<div>
				<SubTitle>Payment Method</SubTitle>
				<PaymentElement
					onChange={(e: StripePaymentElementChangeEvent) =>
						handlePaymentMethodChange(e)
					}
					options={{
						layout: {
							type: 'accordion',
							defaultCollapsed: true,
							radios: false,
							spacedAccordionItems: true
						}
					}}
				/>
				<Flex
					$flexDirection={isMobile ? 'column' : 'row'}
					$alignItems="center"
					$placeContent="center space-between"
					$margin="20px 0 0 0 "
					$columnGap="10px"
					$rowGap="10px"
				>
					<Button
						$isLoading={isLoading || isProcessing}
						onClick={onBack}
						className={`${isMobile ? 'w-100' : ''}`}
						$backgroundColor="#757575"
					>
						GO BACK
					</Button>
					<Button
						$isLoading={isLoading || isProcessing}
						disabled={!stripe || !elements}
						onClick={handleSubmit}
						className={`${isMobile ? 'w-100' : ''}`}
					>
						{selectedPaymentMethod
							? `PAY WITH ${startCase(selectedPaymentMethod).toUpperCase()}`
							: `CONFIRM`}
					</Button>
				</Flex>
			</div>
			<ApproveTransactionModal
				isOpen={showApproveModal}
				message={`Add $${centsToMoney(
					balanceToAdd
				)} to card # ${giftCardNumber}?`}
				toggleHandler={toggleApproveModal}
				title="Confirm transaction"
				confirmHandler={doPayment}
				confirmButtonText="Confirm"
				showCancelButton={true}
				cancelButtonText="Back"
				cancelHandler={onExitApproveModal}
			/>

			<StatusModal
				isOpen={showStatusModal}
				message={statusMessage}
				toggleHandler={closeStatusModel}
			/>
		</>
	);
};
interface IUpdatedCardData {
	id: number;
	cardNumber: string;
	balance: number;
}
